import React, { FC, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';

import { ROUTE_LIST_DETAIL } from 'routes';
import { CardSchema, CollectionCardLinkSchema, UnionExtraData } from 'constants/graphqlTypes';

import { TEST_ACTION_THOUGHT } from 'constants/aqa/pickDetails';
import { getWebDate, getWeeklyDateFormat } from 'helpers/getDateFormat';
import { useReactionContext } from 'components/Reactions/hooks';
import withHapticFeedback from 'helpers/withHapticFeedback';
import useThoughtsModal from 'helpers/useThoughtsModal';
import usePageType from 'helpers/usePageType';
import { isAnimationsDisabled } from 'hooks/useDebugAnimations';

import UILink from 'components/UI/Link';
import ThoughtLastUpdate from 'components/Thoughts/Thought/ThoughtLastUpdate';

import { StyledAction, StyledText, StyledTruncateWrapper } from './styled';

interface IActionContentProps {
  item: CardSchema;
  action: UnionExtraData;
  isDetail?: boolean;
  isSmall?: boolean;
  isVirtualAction?: boolean;
  isUserPicksImmersive?: boolean;
  isImmersive?: boolean;
  isCarousel?: boolean;
}

export const ACTION_WRAPPER_TRUNCATE_CLASS = 'action-truncate-wrapper';

const CHARACTER_LIMIT = 60; // Adjust as needed for your layout

const ActionContent: FC<IActionContentProps> = ({
  action,
  item,
  isDetail,
  isVirtualAction,
  isUserPicksImmersive,
  isImmersive,
  isCarousel: _isCarousel,
  isSmall: _isSmall,
}) => {
  const { id, responseToQuestion, questionGuestList } = item;
  const { isItemDetail } = usePageType();
  const [displayedText, setDisplayedText] = useState('');
  const [charLimit, setCharLimit] = useState<number>(0);

  const user = action?.user;
  const { username = '' } = user ?? {};

  const openThoughts = useThoughtsModal({ id, hideTabs: !isItemDetail, openFromCardDetailPage: isItemDetail });

  const { isQuickReactionOpen } = useReactionContext();

  const handleTruncateClick = () => {
    if (!isVirtualAction && !isQuickReactionOpen) {
      withHapticFeedback(openThoughts)();
    }
  };

  const cardRef = useRef<HTMLDivElement>(null);
  const thoughtTextRef = useRef<HTMLDivElement>(null);
  const textElRef = useRef<HTMLParagraphElement>(null);
  const [shouldAnimateText, setShouldAnimateText] = useState(false);
  const hasAnimatedRef = useRef(false);
  const formatDistanceFromTimestamp = useCallback((timestamp?: string | null) => {
    if (!timestamp) return null;
    const date = getWebDate(timestamp);
    if (Number.isNaN(date.getTime())) return null;
    return formatDistanceToNowStrict(date, {
      addSuffix: true,
      roundingMethod: 'floor',
    });
  }, []);

  const collectionLink = useMemo(() => {
    if (action?.__typename !== 'CollectionCardLinkSchema') return null;
    return (item?.extraData?.items ?? []).find(
      (extra): extra is CollectionCardLinkSchema => extra?.__typename === 'CollectionCardLinkSchema'
    );
  }, [action, item]);

  const collectionLinkDistance = useMemo(() => {
    if (!collectionLink) return null;
    return formatDistanceFromTimestamp(collectionLink.pickCreatedAt ?? collectionLink.createdAt ?? item?.createdAt);
  }, [collectionLink, formatDistanceFromTimestamp, item]);

  // Check if there's a thought associated with this pick
  const hasThought = useMemo(() => {
    return (item?.extraData?.items ?? []).some(
      (extra) => extra?.__typename === 'CardCommentSchema' && (!extra?.isSuspicious || isDetail)
    );
  }, [item, isDetail]);

  // Compute a one-time character limit from the container width once the content is visible
  useEffect(() => {
    if (isDetail || !shouldAnimateText) return;
    const textEl = textElRef.current;
    const containerCandidate = thoughtTextRef.current;
    if (!textEl || !containerCandidate) return;

    const measure = (attempt: number) => {
      let container: HTMLElement | null = containerCandidate.parentElement || containerCandidate;
      let availableWidth = 0;
      for (let i = 0; i < 6 && container; i += 1) {
        const rect = container.getBoundingClientRect();
        if (rect.width > 0) {
          availableWidth = rect.width;
          break;
        }
        container = container.parentElement;
      }
      if (availableWidth <= 0 && cardRef.current) {
        const cardRect = cardRef.current.getBoundingClientRect();
        availableWidth = cardRect.width || 0;
      }
      if (availableWidth <= 0 && attempt < 10) {
        requestAnimationFrame(() => measure(attempt + 1));
        return;
      }
      const widthToUse = availableWidth > 0 ? availableWidth : 239; // fallback to base container width at 383 viewport

      // Linear scale from reference: 239px container -> 60 chars
      const BASE_CONTAINER_WIDTH = 239;
      const BASE_CHAR_LIMIT = CHARACTER_LIMIT; // 60
      const scaled = Math.floor((BASE_CHAR_LIMIT * widthToUse) / BASE_CONTAINER_WIDTH);
      const responsiveLimit = Math.max(BASE_CHAR_LIMIT, scaled);
      setCharLimit(responsiveLimit);
    };

    requestAnimationFrame(() => measure(0));
  }, [isDetail, shouldAnimateText]);

  useEffect(() => {
    if (
      !isDetail &&
      action?.__typename === 'CardCommentSchema' &&
      action.text &&
      shouldAnimateText &&
      charLimit > 0 &&
      !hasAnimatedRef.current
    ) {
      hasAnimatedRef.current = true;
      const limit = Math.max(CHARACTER_LIMIT, charLimit);
      const text = action.text.length > limit ? action.text.slice(0, limit) : action.text;
      const needsEllipsis = action.text.length > limit;
      const textWithQuotes = `"${text}${needsEllipsis ? '...' : '"'}`;

      // Skip typewriter animation if debug mode is enabled
      if (isAnimationsDisabled()) {
        setDisplayedText(textWithQuotes);
        return undefined;
      }

      let currentIndex = 0;
      setDisplayedText('');
      const typewriterInterval = setInterval(() => {
        if (currentIndex < textWithQuotes.length) {
          setDisplayedText(textWithQuotes.slice(0, currentIndex + 1));
          currentIndex += 1;
        } else {
          clearInterval(typewriterInterval);
        }
        return null;
      }, 20);
      return () => clearInterval(typewriterInterval);
    }
    return undefined;
  }, [action, isDetail, shouldAnimateText, charLimit]);

  const actionTypename = action?.__typename;

  useEffect(() => {
    // Reset animation state when card/action changes
    hasAnimatedRef.current = false;
    setShouldAnimateText(false);
    setDisplayedText('');

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldAnimateText(true);
          observer.disconnect();
        }
        // Do NOT set to false on exit
        return null;
      },
      {
        rootMargin: '-112px 0px 0px 0px',
        threshold: 0.1,
      }
    );
    if (thoughtTextRef.current) observer.observe(thoughtTextRef.current);
    return () => observer.disconnect();
  }, [id, actionTypename]);

  const isImmersiveMode = isImmersive === true || isUserPicksImmersive === true;

  return (
    <div ref={cardRef}>
      {action?.__typename === 'CardCommentSchema' && (!action?.isSuspicious || isDetail) && (
        <>
          <StyledTruncateWrapper className={ACTION_WRAPPER_TRUNCATE_CLASS}>
            {isDetail ? (
              action?.text && (
                <StyledText
                  data-testid={TEST_ACTION_THOUGHT}
                  onClickCapture={handleTruncateClick}
                  $isUserPicksImmersive={isImmersiveMode}
                  $isImmersive={isImmersiveMode}
                >
                  &ldquo;{action?.text}&rdquo;
                </StyledText>
              )
            ) : (
              <div ref={thoughtTextRef} style={{ display: 'block' }}>
                <StyledText
                  data-testid={TEST_ACTION_THOUGHT}
                  onClickCapture={handleTruncateClick}
                  ref={textElRef}
                  $isUserPicksImmersive={isImmersiveMode}
                  $isImmersive={isImmersiveMode}
                >
                  {displayedText}
                </StyledText>
              </div>
            )}
          </StyledTruncateWrapper>
          {isImmersiveMode && (
            <ThoughtLastUpdate
              lastUpdate={getWeeklyDateFormat(action.createdAt)}
              username={username}
              parentCard={action?.collection}
              guestListQuestion={questionGuestList}
              responseToQuestion={responseToQuestion}
            />
          )}
        </>
      )}
      {action?.__typename === 'CardPickSchema' && (isItemDetail || hasThought) && (
        <ThoughtLastUpdate
          lastUpdate={getWeeklyDateFormat(action.createdAt)}
          username={username}
          guestListQuestion={questionGuestList}
          responseToQuestion={responseToQuestion}
        />
      )}
      {action?.__typename === 'CardCommentSchema' && action?.isSuspicious && !isDetail && (
        <StyledAction>
          Added &nbsp;
          <b>
            {formatDistanceToNowStrict(getWebDate(action?.createdAt), {
              addSuffix: true,
              roundingMethod: 'floor',
            })}
          </b>
        </StyledAction>
      )}

      {collectionLinkDistance && collectionLink && action?.__typename === 'CollectionCardLinkSchema' && (
        <StyledAction>
          {collectionLinkDistance}&nbsp;&bull;&nbsp;in&nbsp;
          <UILink route={ROUTE_LIST_DETAIL} params={{ listId: action?.collection?.id }}>
            <b>{action?.collection?.title}</b>
          </UILink>
        </StyledAction>
      )}
    </div>
  );
};

export default ActionContent;
